import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PROFILE_CAPABILITIES } from '../../config/profileCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import {
  formatUnreadBadge,
  useNotificationCount,
} from '../../context/NotificationCountContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getMyProfile } from '../../services/profileService';
import type { ProfileView } from '../../types/profile';
import { confirmLogout } from '../../utils/confirmLogout';
import { formatApiError } from '../../utils/formatApiError';

type Props = NativeStackScreenProps<RootStackParamList, 'Profile'>;

type MenuIcon = 'person' | 'lock' | 'activity';

function displayValue(fieldValue: unknown) {
  return fieldValue === null || fieldValue === undefined || fieldValue === ''
    ? 'Not available'
    : String(fieldValue);
}

function formatRoleLabel(roles: string[] | undefined): string {
  if (!roles?.length) return 'Admin';
  return roles
    .map(role =>
      role
        .toLowerCase()
        .split(/[_\s]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '),
    )
    .join(', ');
}

function MenuGlyph({ icon, color }: { icon: MenuIcon; color: string }) {
  if (icon === 'lock') {
    return (
      <View style={iconStyles.lockRoot} accessibilityElementsHidden>
        <View style={[iconStyles.lockShackle, { borderColor: color }]} />
        <View style={[iconStyles.lockBody, { borderColor: color }]} />
      </View>
    );
  }
  if (icon === 'activity') {
    return (
      <View style={iconStyles.shieldRoot} accessibilityElementsHidden>
        <View style={[iconStyles.shield, { borderColor: color }]}>
          <View style={[iconStyles.shieldCheck, { borderColor: color }]} />
        </View>
      </View>
    );
  }
  return (
    <View style={iconStyles.personRoot} accessibilityElementsHidden>
      <View style={[iconStyles.personHead, { borderColor: color }]} />
      <View style={[iconStyles.personBody, { borderColor: color }]} />
    </View>
  );
}

function PhoneGlyph({ color }: { color: string }) {
  return (
    <View style={iconStyles.phoneRoot} accessibilityElementsHidden>
      <View style={[iconStyles.phoneBody, { borderColor: color }]} />
    </View>
  );
}

function BellGlyph({ color }: { color: string }) {
  return (
    <View style={iconStyles.bellRoot} accessibilityElementsHidden>
      <View style={[iconStyles.bellDome, { borderColor: color }]} />
      <View style={[iconStyles.bellClapper, { backgroundColor: color }]} />
    </View>
  );
}

function MenuRow({
  title,
  icon,
  palette,
  showDivider,
  onPress,
}: {
  title: string;
  icon: MenuIcon;
  palette: ColorPalette;
  showDivider: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      onPress={onPress}
      style={({ pressed }) => [
        styles.menuRow,
        {
          borderBottomColor: palette.border,
          borderBottomWidth: showDivider ? StyleSheet.hairlineWidth : 0,
          opacity: pressed ? 0.72 : 1,
        },
      ]}
    >
      <MenuGlyph icon={icon} color={palette.textMuted} />
      <Text style={[styles.menuLabel, { color: palette.textPrimary }]}>
        {title}
      </Text>
      <Text style={[styles.chevron, { color: palette.textMuted }]}>›</Text>
    </Pressable>
  );
}

export function ProfileScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const notificationCount = useNotificationCount();
  const unreadBadge = formatUnreadBadge(notificationCount.unreadCount);
  const canViewEmployees = auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW);
  const canViewOrganization = auth.hasPermission(PERMISSIONS.SETTINGS_VIEW);
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [logoutError, setLogoutError] = useState('');

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError('');
      try {
        const next = await getMyProfile({
          canViewEmployees,
          canViewOrganization,
        });
        setProfile(next);
      } catch (caught) {
        setError(
          formatApiError(caught).message || 'Unable to load your profile.',
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [canViewEmployees, canViewOrganization],
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  async function logout() {
    setLogoutError('');
    try {
      await auth.signOut();
    } catch {
      setLogoutError('Signed out locally, but the server could not be reached.');
    }
  }

  const displayName = displayValue(
    profile?.displayName ?? auth.user?.displayName ?? auth.user?.username,
  );
  const roleLabel = formatRoleLabel(profile?.roles ?? auth.roles);
  const email = displayValue(profile?.email ?? auth.user?.email);
  const phone = displayValue(profile?.mobile ?? auth.user?.phoneNumber);

  const themed = useMemo(
    () => ({
      name: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
      meta: { ...typography.caption, color: palette.textMuted },
      error: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
        textAlign: 'center' as const,
      },
      logout: {
        ...typography.button,
        color: palette.error,
      },
    }),
    [palette],
  );

  if (loading && !profile) {
    return (
      <AppScreen backgroundColor={palette.surface}>
        <AppLoader message="Loading profile" />
      </AppScreen>
    );
  }

  if (error && !profile) {
    return (
      <AppScreen backgroundColor={palette.surface}>
        <ErrorState message={error} onRetry={() => load()} />
      </AppScreen>
    );
  }

  return (
    <AppScreen
      scroll
      backgroundColor={palette.surface}
      contentStyle={styles.screen}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />
      }
    >
      <ScreenHeader
        bleed={false}
        title="Profile"
        onBack={navigation.goBack}
        right={
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={
              unreadBadge
                ? `Notifications, ${unreadBadge} unread`
                : 'Notifications'
            }
            onPress={() => navigation.navigate(routes.notificationList)}
            hitSlop={8}
          >
            <BellGlyph color={palette.white} />
            {unreadBadge ? (
              <View
                style={[styles.badge, { backgroundColor: palette.error }]}
                accessibilityElementsHidden
              >
                <Text style={[styles.badgeText, { color: palette.onError }]}>
                  {unreadBadge}
                </Text>
              </View>
            ) : null}
          </Pressable>
        }
      />

      <View style={styles.body}>
        <View style={styles.summary}>
          <ProfileAvatar name={displayName} size={72} />
          <View style={styles.summaryText}>
            <Text accessibilityRole="header" style={themed.name}>
              {displayName}
            </Text>
            <Text style={themed.meta}>{roleLabel}</Text>
            <Text style={themed.meta}>{email}</Text>
            <View style={styles.phoneRow}>
              <PhoneGlyph color={palette.textMuted} />
              <Text style={themed.meta}>{phone}</Text>
            </View>
          </View>
        </View>

        <View style={styles.menu}>
          <MenuRow
            title="My Profile"
            icon="person"
            palette={palette}
            showDivider
            onPress={() => navigation.navigate(routes.profileDetails)}
          />
          {PROFILE_CAPABILITIES.changePassword ? (
            <MenuRow
              title="Change Password"
              icon="lock"
              palette={palette}
              showDivider
              onPress={() => navigation.navigate(routes.changePassword)}
            />
          ) : null}
          <MenuRow
            title="System Activity"
            icon="activity"
            palette={palette}
            showDivider={false}
            onPress={() => {
              Alert.alert(
                'System Activity',
                'System activity is not available yet.',
              );
            }}
          />
        </View>

        {logoutError ? (
          <Text accessibilityRole="alert" style={themed.error}>
            {logoutError}
          </Text>
        ) : null}

        {PROFILE_CAPABILITIES.logout ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Logout"
            onPress={() => confirmLogout({ onConfirm: logout })}
            style={({ pressed }) => [
              styles.logoutButton,
              {
                borderColor: palette.error,
                backgroundColor: palette.surface,
                opacity: pressed ? 0.82 : 1,
              },
            ]}
          >
            <Text style={themed.logout}>Logout</Text>
          </Pressable>
        ) : null}
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  screen: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -6,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 9,
    lineHeight: 11,
    fontWeight: '700',
  },
  body: {
    flexGrow: 1,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xxl,
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
    marginBottom: spacing.xl,
  },
  summaryText: {
    flex: 1,
    gap: spacing.xs,
  },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  menu: {
    marginBottom: spacing.xxl,
  },
  menuRow: {
    minHeight: 56,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
  },
  menuLabel: {
    ...typography.body,
    flex: 1,
    fontWeight: '500',
  },
  chevron: {
    fontSize: 28,
    lineHeight: 28,
    fontWeight: '300',
    marginTop: -2,
  },
  logoutButton: {
    marginTop: 'auto',
    minHeight: 50,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
});

const iconStyles = StyleSheet.create({
  personRoot: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  personHead: {
    width: 8,
    height: 8,
    borderRadius: 4,
    borderWidth: 1.5,
    marginBottom: 2,
  },
  personBody: {
    width: 16,
    height: 9,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderWidth: 1.5,
    borderBottomWidth: 0,
  },
  lockRoot: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  lockShackle: {
    width: 10,
    height: 8,
    borderWidth: 1.5,
    borderBottomWidth: 0,
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
    marginBottom: -1,
  },
  lockBody: {
    width: 14,
    height: 11,
    borderWidth: 1.5,
    borderRadius: 3,
  },
  shieldRoot: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shield: {
    width: 14,
    height: 16,
    borderWidth: 1.5,
    borderRadius: 3,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shieldCheck: {
    width: 6,
    height: 9,
    borderRightWidth: 1.5,
    borderBottomWidth: 1.5,
    transform: [{ rotate: '45deg' }],
    marginTop: -2,
  },
  phoneRoot: {
    width: 14,
    height: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  phoneBody: {
    width: 10,
    height: 14,
    borderWidth: 1.5,
    borderRadius: 2,
  },
  bellRoot: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  bellDome: {
    width: 14,
    height: 12,
    borderWidth: 1.5,
    borderBottomWidth: 0,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  bellClapper: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 1,
  },
});
